import { Calendar } from "lucide-react";
import { ReactNode } from "react";
import { VERSION_DETAILS_COPY } from "../constants/versionDetails.constants";
import { toVersionDetailsFormValues } from "../helpers/versionDetails.helpers";
import { VersionDetailsDraft, VersionHistoryGroup } from "../types/versionHistory.types";
import {
  formatPricePerUnit,
  formatReportingYear,
  formatUnits,
  hasVersionDetails
} from "../utils/versionDetails.utils";
import { VersionDetailsForm } from "./VersionDetailsForm";

/** One read-only label/value pair. Local to this section -- nothing else renders a saved value. */
const DetailValue = ({
  label,
  value,
  icon
}: {
  label: string;
  value: string;
  icon?: ReactNode;
}) => (
  <div className="tw-space-y-1">
    <p className="tw-text-[10.5px] tw-uppercase tw-tracking-[0.08em] tw-font-mono tw-text-muted-foreground">
      {label}
    </p>
    <div className="tw-h-10 tw-px-3 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-mono tw-text-foreground tw-border tw-border-border tw-rounded-md tw-min-w-[130px]">
      {icon}
      {value}
    </div>
  </div>
);

interface VersionDetailsSectionProps {
  version: VersionHistoryGroup;
  yearOptions: number[];
  currencyCode: string;
  versionLabel: string;
  isEditing: boolean;
  isSaving: boolean;
  onCommit: (draft: VersionDetailsDraft) => Promise<string | null>;
  onCancel: () => void;
  onRequestDelete: () => void;
}

/**
 * The "Version details" block under an expanded version: heading plus either the saved values
 * or the editor. Owns the mode switch and nothing else.
 */
export const VersionDetailsSection = ({
  version,
  yearOptions,
  currencyCode,
  versionLabel,
  isEditing,
  isSaving,
  onCommit,
  onCancel,
  onRequestDelete
}: VersionDetailsSectionProps) => (
  <div className="tw-space-y-2.5">
    <p className="tw-text-[10.5px] tw-uppercase tw-tracking-[0.08em] tw-font-mono tw-text-muted-foreground">
      {VERSION_DETAILS_COPY.sectionTitle}
    </p>

    {isEditing ? (
      <VersionDetailsForm
        // Remount per version so a different row's saved values seed the draft correctly.
        key={version.versionId ?? ""}
        initialValues={toVersionDetailsFormValues(version)}
        yearOptions={yearOptions}
        currencyCode={currencyCode}
        versionLabel={versionLabel}
        isSaving={isSaving}
        canDelete={hasVersionDetails(version)}
        onSubmit={onCommit}
        onCancel={onCancel}
        onRequestDelete={onRequestDelete}
      />
    ) : (
      <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4">
        <DetailValue
          label={VERSION_DETAILS_COPY.yearLabel}
          value={formatReportingYear(version.reportingYear)}
          icon={<Calendar className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-muted-foreground" />}
        />
        <DetailValue
          label={VERSION_DETAILS_COPY.unitsLabel}
          value={formatUnits(version.numberOfUnits)}
        />
        <DetailValue
          label={VERSION_DETAILS_COPY.priceLabel}
          value={formatPricePerUnit(version.pricePerUnit, currencyCode)}
        />
      </div>
    )}
  </div>
);
